import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { StatusText } from "@/shared/components/ui/status-chip";
import { useLead, useLeadActivity, useSetLeadStatus } from "../use-leads";
import { useEnrichLead } from "../use-enrichment";
import { Section, Field, CompanyBlock, RawBlock } from "./detail-parts";
import { statusTone } from "./lead-status";
import { ContactsPanel } from "./EngagementTimeline";
import type { LeadDetail } from "../types";

/**
 * Full lead profile (`/leads/:id`) — the whole record for one lead, data-first rather
 * than email-first: a balanced header (qualification + reachability), then contacts with
 * their Smartlead engagement timeline, company firmographics, business details, and the
 * pipeline/screening + raw provenance as a sidebar. The list drawer is the quick peek;
 * this is the deep view it links to.
 */
export function LeadProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useLead(id);
  const activity = useLeadActivity(id);
  const set = useSetLeadStatus();
  const enrich = useEnrichLead();

  return (
    <div className="mx-auto max-w-[1100px]">
      <Link
        to="/leads"
        data-testid="profile-back"
        className="mb-4 inline-flex items-center gap-1 text-[12px] font-medium text-ge-muted hover:text-ge-primary"
      >
        <ArrowLeft className="h-[13px] w-[13px]" /> All leads
      </Link>

      {isError ? (
        <p className="text-[13px] text-ge-danger">Couldn&apos;t load this lead.</p>
      ) : isLoading || !data ? (
        <div className="space-y-3" aria-hidden data-testid="profile-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-[16px] w-full animate-pulse rounded bg-ge-border-card" />
          ))}
        </div>
      ) : (
        <div className="space-y-6" data-testid="lead-profile">
          <ProfileHeader
            lead={data}
            onApprove={() => id && set.mutate({ id, action: "approve" })}
            onReject={() => id && set.mutate({ id, action: "reject" })}
            onEnrich={() => id && enrich.mutate(id)}
            busy={set.isPending}
            enriching={enrich.isPending}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
            {/* Main column */}
            <div className="space-y-6">
              <Card className="p-[18px]">
                <Section
                  title={`Contacts & engagement (${data.contacts.length})`}
                  hint="Emails found for this lead and their outreach activity (opens, clicks, replies) reported by the sending provider."
                >
                  <ContactsPanel
                    contacts={data.contacts}
                    activity={activity.data}
                    activityLoading={activity.isLoading}
                    activityError={activity.isError}
                  />
                </Section>
              </Card>

              <Card className="p-[18px]">
                <CompanyBlock aiData={data.aiData} />
                <div className="mt-4">
                  <Section title="Business details">
                    <Field label="Website" value={data.website} link />
                    <Field label="Phone" value={data.phone} />
                    <Field label="Address" value={data.address} />
                    <Field label="City" value={cityLabel(data)} />
                    <Field label="Rating" value={data.rating != null ? data.rating.toFixed(1) : null} />
                    <Field label="Source" value={data.source} />
                    <Field
                      label="Confidence"
                      value={data.confidence != null ? `${Math.round(data.confidence * 100)}%` : null}
                    />
                    <Field
                      label="Enriched"
                      value={data.enrichedAt ? new Date(data.enrichedAt).toLocaleString() : "not yet"}
                    />
                    <Field label="Discovered" value={new Date(data.createdAt).toLocaleString()} />
                  </Section>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="p-[18px]">
                <Section title="Pipeline & screening" hint="Why this lead is here and how it was screened — status, dedup key, and the discovery run it came from.">
                  <Field label="Status" value={data.status} />
                  <Field label="Reason" value={data.screenReason} />
                  <Field label="Dedup key" value={data.dedupKey} mono />
                  <Field label="Discovery run" value={data.discoveryRunId} mono />
                </Section>
              </Card>

              {data.campaignLeads.length > 0 && (
                <Card className="p-[18px]">
                  <Section title="Campaigns">
                    <ul className="space-y-1" data-testid="profile-campaigns">
                      {data.campaignLeads.map((cl) => (
                        <li key={cl.id} className="flex items-center justify-between font-mono text-[12px]">
                          <span className="truncate text-ge-value">{cl.campaign?.name ?? cl.campaignId}</span>
                          {cl.campaign?.status && <span className="text-ge-faint">{cl.campaign.status}</span>}
                        </li>
                      ))}
                    </ul>
                  </Section>
                </Card>
              )}

              <Card className="space-y-3 p-[18px]">
                <RawBlock title="Raw provenance" value={data.provenance} />
                <RawBlock title="AI data" value={data.aiData} />
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileHeader({
  lead,
  onApprove,
  onReject,
  onEnrich,
  busy,
  enriching,
}: {
  lead: LeadDetail;
  onApprove: () => void;
  onReject: () => void;
  onEnrich: () => void;
  busy: boolean;
  enriching: boolean;
}) {
  const company = (lead.aiData as { enrichment?: { company?: Record<string, unknown> } } | null)?.enrichment
    ?.company;
  const qualification = [
    strOrNull(company?.industry),
    company?.headcount != null ? `${company.headcount} staff` : null,
    strOrNull(company?.type),
    lead.rating != null ? `★ ${lead.rating.toFixed(1)}` : null,
  ].filter(Boolean) as string[];

  const verified = lead.contacts.find((c) => c.verified && !c.optedOut);
  const reach = [
    verified ? "verified email" : lead.contacts.some((c) => c.email) ? "email found" : "no email",
    lead.phone ? "phone" : null,
    `${lead.contacts.length} contact${lead.contacts.length === 1 ? "" : "s"}`,
  ].filter(Boolean) as string[];

  return (
    <Card className="p-[20px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="ge-eyebrow">LEAD</p>
          <div className="flex items-center gap-3">
            <h1 className="truncate text-[22px] font-bold tracking-[-0.3px] text-ge-primary">{lead.name}</h1>
            <StatusText tone={statusTone(lead.status)}>{lead.status}</StatusText>
          </div>
          {qualification.length > 0 && (
            <p className="mt-1 text-[12.5px] text-ge-secondary" data-testid="hero-qualification">
              {qualification.join(" · ")}
            </p>
          )}
          <p className="mt-0.5 font-mono text-[11.5px] text-ge-muted" data-testid="hero-reach">
            {reach.join(" · ")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="accent-outline"
            size="sm"
            disabled={enriching || busy || lead.status !== "APPROVED"}
            onClick={onEnrich}
            data-testid="profile-enrich"
            title={
              lead.status === "APPROVED"
                ? "Search Hunter for contacts and company data"
                : "Approve the lead first to enrich it"
            }
          >
            <Mail className="h-[13px] w-[13px]" strokeWidth={2.2} />
            {enriching ? "Enriching…" : lead.contacts.length > 0 ? "Re-enrich" : "Find emails"}
          </Button>
          <Button
            variant="accent-outline"
            size="sm"
            disabled={busy || enriching || lead.status === "APPROVED"}
            onClick={onApprove}
            data-testid="profile-approve"
          >
            {lead.status === "APPROVED" ? "Approved" : "Approve"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy || enriching || lead.status === "REJECTED"}
            onClick={onReject}
            data-testid="profile-reject"
          >
            {lead.status === "REJECTED" ? "Rejected" : "Reject"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function cityLabel(lead: LeadDetail): string | null {
  if (!lead.city) return null;
  return lead.city.state ? `${lead.city.name}, ${lead.city.state}` : lead.city.name;
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}
